import { v4 as uuidv4 } from 'uuid';
export function createListItem(data) {
    return {
        id: uuidv4(),
        text: data.text,
        quantity: data.quantity ?? null,
        notes: data.notes ?? null,
        category: data.category ?? null,
        url: data.url ?? null,
        tags: data.tags ?? [],
        // Children keep their typed order unless one was given.
        children: (data.children ?? []).map((c, i) => createListItem({ ...c, order: c.order ?? i })),
        order: data.order ?? 0,
        completed_at: null,
        price: data.price ?? null,
        rating: data.rating ?? null,
        priority: data.priority ?? null,
        location: data.location ?? null,
        due_date: data.due_date ?? null,
        source: data.source ?? null,
        image_url: data.image_url ?? null,
    };
}
export function createList(data) {
    const now = new Date().toISOString();
    return {
        id: uuidv4(),
        name: data.name,
        description: data.description ?? null,
        list_type: data.list_type ?? 'eternal',
        items: [],
        categories: data.categories ?? [],
        linked_habit_ids: data.linked_habit_ids ?? [],
        linked_project_ids: data.linked_project_ids ?? [],
        linked_goal_ids: data.linked_goal_ids ?? [],
        created_at: now,
        updated_at: now,
        archived: false,
        is_pinned: data.is_pinned ?? false,
        color: data.color ?? null,
        icon: data.icon ?? null,
        default_sort: data.default_sort ?? null,
        completed_at: null,
    };
}
export function createListInstance(data, templateName, templateItems = []) {
    const now = new Date().toISOString();
    return {
        id: uuidv4(),
        list_id: data.list_id,
        name: data.name ?? `${templateName ?? 'List'} - ${new Date().toLocaleDateString()}`,
        // Snapshot the template so the run is independent from the start
        items: cloneItemsForInstance(templateItems),
        item_states: {},
        extra_items: [],
        linked_habit_instance_id: data.linked_habit_instance_id ?? null,
        created_at: now,
        completed_at: null,
        archived_at: null,
    };
}
/**
 * Deep-copy template items for a new run. Ids are preserved so that legacy
 * `item_states` written by older clients still line up, and so a run can be
 * traced back to the template rows it came from. Checked state is cleared —
 * a new run starts empty.
 */
export function cloneItemsForInstance(items) {
    return items.map((item) => ({
        ...item,
        completed_at: null,
        children: item.children ? cloneItemsForInstance(item.children) : [],
    }));
}
/**
 * The effective item list for an instance.
 *
 * New instances carry their own `items`. Instances created before that
 * existed are resolved here from the template + the legacy `item_states`
 * map + `extra_items`, so old runs keep working and get migrated the first
 * time anything writes to them.
 */
export function resolveInstanceItems(instance, template) {
    if (instance.items)
        return instance.items;
    const applyLegacyState = (item) => ({
        ...item,
        completed_at: instance.item_states?.[item.id] ? instance.created_at : null,
        children: item.children ? item.children.map(applyLegacyState) : [],
    });
    return [
        ...(template?.items ?? []).map(applyLegacyState),
        ...(instance.extra_items ?? []).map(applyLegacyState),
    ];
}
/**
 * Item-tree helpers shared by lists and checklist runs, the app and the
 * assistant. Both hold the same `ListItem[]` shape (a run IS a list), so the
 * recursion lives here once. The assistant used to touch top-level items only,
 * so a sub-item could not be ticked by voice.
 */
export function mapItemDeep(items, itemId, fn) {
    return items.map((item) => {
        if (item.id === itemId)
            return fn(item);
        if (item.children && item.children.length > 0) {
            return { ...item, children: mapItemDeep(item.children, itemId, fn) };
        }
        return item;
    });
}
export function removeItemDeep(items, itemId) {
    return items
        .filter((item) => item.id !== itemId)
        .map((item, idx) => ({
        ...item,
        order: idx,
        children: item.children ? removeItemDeep(item.children, itemId) : [],
    }));
}
/** Every item, at any depth, that matches — parents before their children. */
export function findItemsDeep(items, match) {
    const found = [];
    const walk = (level) => {
        for (const item of level) {
            if (match(item))
                found.push(item);
            if (item.children?.length)
                walk(item.children);
        }
    };
    walk(items);
    return found;
}
/**
 * A checklist's run in progress: the newest one neither finished nor
 * archived. Ticking an item on a checklist means ticking it on this run —
 * the template's own items are never checked off in the app.
 */
export function currentRun(listId, instances) {
    return instances
        .filter((i) => i.list_id === listId && !i.completed_at && !i.archived_at)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
}
//# sourceMappingURL=list.js.map